<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="headline primary--text">
              <v-icon class="mr-2">mdi-briefcase</v-icon>
              Work Histories
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                elevation="2"
                small
                to="/work_histories/create"
                class="mr-2"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add Work History
              </v-btn>
              <v-btn
                color="success"
                elevation="2"
                small
                @click="exportToExcel"
                :loading="exporting"
              >
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Export
              </v-btn>
            </v-card-title>

            <v-card-text>
              <!-- Filters -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filters.search_keyword"
                    label="Search"
                    prepend-inner-icon="mdi-magnify"
                    solo
                    rounded
                    dense
                    clearable
                    @input="search"
                    @click:clear="clearSearch"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.user_id"
                    :items="users"
                    item-text="name"
                    item-value="id"
                    label="Filter by Employee"
                    solo
                    rounded
                    dense
                    clearable
                    @change="getWorkHistories"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.is_active"
                    :items="statusOptions"
                    label="Status"
                    solo
                    rounded
                    dense
                    clearable
                    @change="getWorkHistories"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.current_only"
                    :items="currentPositionOptions"
                    label="Position Type"
                    solo
                    rounded
                    dense
                    clearable
                    @change="getWorkHistories"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Data Table -->
              <v-data-table
                :headers="headers"
                :items="workHistories"
                :loading="loading"
                :server-items-length="totalItems"
                :page.sync="page"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{
                  itemsPerPageOptions: [10, 25, 50, 100]
                }"
                fixed-header
                height="285"
                class="elevation-1"
                @update:page="getWorkHistories"
                @update:items-per-page="getWorkHistories"
              >
                <!-- Employee Column -->
                <template v-slot:[`item.user`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.user && item.user.image_path"
                        :src="getImageUrl(item.user.image_path)"
                        :alt="item.user.first_name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="body-2 font-weight-medium">
                        {{ getUserName(item.user) }}
                      </div>
                      <div class="caption grey--text">
                        {{ item.user ? item.user.email : 'N/A' }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Organization Column -->
                <template v-slot:[`item.organization_name`]="{ item }">
                  <div>
                    <div class="body-2 font-weight-medium">
                      {{ item.organization_name || 'N/A' }}
                    </div>
                    <div class="caption grey--text">
                      {{ item.job_title || 'N/A' }}
                    </div>
                  </div>
                </template>

                <!-- Duration Column -->
                <template v-slot:[`item.duration`]="{ item }">
                  <div>
                    <div class="body-2">
                      {{ formatDateRange(item.start_date, item.end_date) }}
                    </div>
                    <v-chip
                      v-if="!item.end_date"
                      x-small
                      color="success"
                      text-color="white"
                    >
                      Current
                    </v-chip>
                  </div>
                </template>

                <!-- Status Column -->
                <template v-slot:[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    text-color="white"
                  >
                    {{ item.is_active ? 'Active' : 'Inactive' }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        :to="`/work_histories/${encryptIt(item.id)}`"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>
                  
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="confirmDelete(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </template>

                <!-- No data template -->
                <template v-slot:no-data>
                  <empty-state icon="mdi-briefcase" title="No work histories found" message="Start by adding your first work history entry" />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this work history entry?
          <br><strong>{{ selectedItem?.organization_name }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteWorkHistory">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "WorkHistoriesIndex",
  data() {
    return {
      loading: false,
      exporting: false,
      workHistories: [],
      users: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 10,
      deleteDialog: false,
      selectedItem: null,
      
      // Filters
      filters: {
        search_keyword: "",
        user_id: null,
        is_active: null,
        current_only: null,
      },
      
      // Options
      statusOptions: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      currentPositionOptions: [
        { text: "Current Positions", value: true },
        { text: "Previous Positions", value: false },
      ],
      
      // Table Headers
      headers: [
        { text: "Employee", value: "user", width: "200px" },
        { text: "Organization & Role", value: "organization_name", width: "250px" },
        { text: "Duration", value: "duration", width: "200px" },
        { text: "City", value: "city", width: "150px" },
        { text: "Status", value: "is_active", width: "100px" },
        { text: "Actions", value: "actions", sortable: false, width: "100px" },
      ],
    };
  },

  mounted() {
    this.getWorkHistories();
    this.getUsers();
  },

  methods: {
    async getWorkHistories() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          ...this.filters,
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await axios.get("/work_histories", { params });
        
        this.workHistories = response.data.data;
        this.totalItems = response.data.count;
      } catch (error) {
        console.error("Error fetching work histories:", error);
        this.$toast.error("Failed to load work histories");
      } finally {
        this.loading = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: 1000 }
        });
        
        this.users = response.data.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    search() {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.getWorkHistories();
      }, 500);
    },

    clearSearch() {
      this.filters.search_keyword = "";
      this.page = 1;
      this.getWorkHistories();
    },

    confirmDelete(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    async deleteWorkHistory() {
      try {
        await axios.post(`/work_histories/delete/${this.selectedItem.id}`);
        this.$toast.success("Work history deleted successfully");
        this.deleteDialog = false;
        this.selectedItem = null;
        this.getWorkHistories();
      } catch (error) {
        console.error("Error deleting work history:", error);
        this.$toast.error("Failed to delete work history");
      }
    },

    async exportToExcel() {
      this.exporting = true;
      try {
        const params = { ...this.filters };
        
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await axios.get("/work_histories/excel", {
          params,
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `work_histories_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$toast.success("Excel file downloaded successfully");
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$toast.error("Failed to export data");
      } finally {
        this.exporting = false;
      }
    },

    getUserName(user) {
      if (!user) return 'N/A';
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'N/A';
    },

    getImageUrl(imagePath) {
      return this.mediaUrl + '/' + imagePath;
    },

    formatDateRange(startDate, endDate) {
      if (!startDate) return 'N/A';
      
      const start = new Date(startDate).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
      
      if (!endDate) {
        return `${start} - Present`;
      }
      
      const end = new Date(endDate).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
      
      return `${start} - ${end}`;
    },
  },

  watch: {
    page() {
      this.getWorkHistories();
    },
    itemsPerPage() {
      this.page = 1;
      this.getWorkHistories();
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.v-data-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(33, 150, 243, 0.04) !important;
}
</style>